import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ArrowLeft, MessageCircle, Languages, Sparkles,
  Building2, User, Phone, Send, CheckCircle2, TrendingUp, Wallet,
  Lock, FileCheck, BadgeCheck, Briefcase, Truck, Gauge, Activity, Menu
} from 'lucide-react';
import { MarketingMobileNavDrawer } from '../components/MarketingMobileNavDrawer';
import { toast } from '../hooks/use-toast';
import { translations } from '../i18n/translations';
import { getPublicApiBaseUrl } from '../utils/publicApi';
import { trackGoogleAdsFormConversion } from '../utils/googleAds';
import { BRAND_ICON_SRC } from '../constants/brand';

const FleetSaveLanding = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('autogoLanguage') || 'es');
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [transfers, setTransfers] = useState(20);
  const [avgKm, setAvgKm] = useState(350);
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', monthlyTransfers: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const t = translations[language];
  const f = t.fleetSaveLanding;
  const fleetMobileNavLinks = [
    { to: '/', label: f.backHome, testId: 'fs-mnav-home' },
    { href: '#fs-how', label: f.hero.ctaSecondary, testId: 'fs-mnav-how' },
    { href: '#fs-form', label: f.navCta, testId: 'fs-mnav-form' },
    { to: '/recuperacion', label: t.nav.recovery, testId: 'fs-mnav-recovery' },
  ];
  const revealRefs = useRef([]);

  useEffect(() => {
    document.title = f.meta.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', f.meta.description);
  }, [f]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('autogoLanguage', next);
      return next;
    });
  };

  // Savings calculator: price per transfer ≈ 80€ base + 0.36€/km, 10% savings applied
  const savings = useMemo(() => {
    const pricePerTransfer = 80 + avgKm * 0.36;
    const monthlyCost = transfers * pricePerTransfer;
    const annualSavings = Math.round(monthlyCost * 12 * 0.1);
    const monthlySavings = Math.round(annualSavings / 12);
    return { annual: annualSavings, monthly: monthlySavings };
  }, [transfers, avgKm]);

  const fmtEUR = (n) => n.toLocaleString(language === 'es' ? 'es-ES' : 'en-US');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${getPublicApiBaseUrl()}/api/operating-margin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: form.company,
          name: form.name,
          email: form.email,
          phone: form.phone,
          annualKm: '',
          annualTransports: form.monthlyTransfers,
          notes: `Lead Fleet Save · calc estimado: ${fmtEUR(savings.annual)}€/año (${transfers} traslados/mes · ${avgKm} km media)`
        })
      });
      if (!res.ok) throw new Error();
      if (window.fbq) {
        window.fbq('track', 'Lead', { content_name: 'Fleet Save Proposal', content_category: 'B2B Fleet', value: 0, currency: 'EUR' });
      }
      trackGoogleAdsFormConversion();
      toast({ title: f.form.successTitle, description: f.form.successDescription });
      setSent(true);
    } catch {
      toast({ title: f.form.errorTitle, description: f.form.errorDescription, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = () => {
    const text = language === 'es'
      ? `Hola! Me interesa Tarifa flota (Fleet Save). Mi volumen estimado es ~${transfers} traslados/mes.`
      : `Hello! I'm interested in Fleet Save. My estimated volume is ~${transfers} transfers/month.`;
    if (window.fbq) window.fbq('track', 'Contact', { content_name: 'Fleet Save WhatsApp', content_category: 'B2B Fleet' });
    window.open(`https://wa.me/34930491501?text=${encodeURIComponent(text)}`, '_blank');
  };

  const forWhoIcons = [Briefcase, Truck, Building2];

  return (
    <div className="relative min-h-screen text-[#F8FAFC] overflow-x-hidden" style={{ background: '#060B14' }}>
      <div className="ag-grain" />
      <div className="fixed inset-0 ag-grid-bg opacity-[0.35] pointer-events-none" />
      <div className="ag-radial-spot" style={{ top: '-200px', left: '-150px', width: '600px', height: '600px', background: 'rgba(0, 242, 96, 0.18)' }} />
      <div className="ag-radial-spot" style={{ top: '25%', right: '-200px', width: '700px', height: '700px', background: 'rgba(27, 147, 164, 0.14)' }} />

      {/* Top stack: safe area + banner + header */}
      <div
        className="fixed top-0 inset-x-0 z-50 flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        data-testid="fs-marketing-top-stack"
      >
        <div
          className="ag-glass-strong shrink-0 border-b border-white/5 py-2 text-center text-[11px] font-mono uppercase tracking-[0.18em] sm:text-xs"
          data-testid="fs-top-banner"
        >
          <span className="inline-flex max-w-[100vw] items-center gap-2 px-2 text-[#CBD5E1]">
            <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-[#00F260] ag-pulse" />
            <span className="truncate">{f.topBanner}</span>
          </span>
        </div>

        <header
          className={`shrink-0 transition-all duration-300 ag-glass-strong ${scrolled ? 'border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.35)]' : 'border-b border-white/5'}`}
          data-testid="fs-header"
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-4 md:h-20 md:px-10">
            <Link to="/" className="group flex min-w-0 shrink-0 items-center gap-2 sm:gap-3" data-testid="fs-logo-link">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 ag-glass-strong sm:h-10 sm:w-10">
                <img src={BRAND_ICON_SRC} alt="AutoGo" className="h-6 w-6 object-contain sm:h-7 sm:w-7" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#00F260]/20 to-transparent" />
              </div>
              <div className="min-w-0 leading-none">
                <div className="font-display text-base font-extrabold tracking-tight sm:text-lg md:text-xl">AUTOGO</div>
                <div className="mt-0.5 font-mono text-[8px] tracking-[0.22em] text-[#00F260] sm:text-[9px] sm:tracking-[0.25em]">{f.brandTag}</div>
              </div>
            </Link>

            <Link
              to="/"
              className="hidden items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] transition-colors hover:text-white md:inline-flex"
              data-testid="fs-back-home"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {f.backHome}
            </Link>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 text-white/90 hover:border-[#00F260]/50 hover:bg-white/[0.06] md:hidden"
                aria-label={language === 'es' ? 'Abrir menú' : 'Open menu'}
                aria-expanded={mobileNavOpen}
                data-testid="fs-mobile-menu-open"
              >
                <Menu className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={toggleLanguage}
                className="hidden items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] transition-all hover:border-[#00F260]/40 hover:text-white sm:flex"
                data-testid="fs-lang"
              >
                <Languages className="h-3.5 w-3.5" />
                {language}
              </button>
              <a
                href="#fs-form"
                className="group relative inline-flex items-center gap-1.5 rounded-full bg-[#00F260] px-3 py-2 text-xs font-semibold text-[#060B14] transition-colors hover:bg-white sm:gap-2 sm:px-5 sm:py-2.5 sm:text-sm"
                data-testid="fs-header-cta"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                <span className="sm:hidden">{language === 'es' ? 'Propuesta' : 'Proposal'}</span>
                <span className="hidden sm:inline">{f.navCta}</span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </header>
      </div>

      <MarketingMobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        links={fleetMobileNavLinks}
        language={language}
        onToggleLanguage={toggleLanguage}
        partnersTitle={t.clients.tagline}
      />

      {/* 01 HERO */}
      <section
        className="relative px-5 pb-16 pt-[calc(140px+env(safe-area-inset-top,0px))] md:px-10 md:pb-24 md:pt-[calc(170px+env(safe-area-inset-top,0px))]"
        data-testid="fs-hero"
      >
        <div className="max-w-6xl mx-auto text-center ag-animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00F260]/10 border border-[#00F260]/30 rounded-full text-[10px] md:text-[11px] tracking-[0.25em] font-mono text-[#00F260] mb-7">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#00F260] ag-pulse" />
            {f.hero.badge}
          </div>
          <h1 className="font-display text-[40px] sm:text-6xl lg:text-[86px] xl:text-[98px] font-extrabold leading-[0.95] tracking-tight">
            <span className="block text-white">{f.hero.title} <span className="ag-shine-text">{f.hero.titleAccent1}</span></span>
            <span className="block text-white mt-1">{f.hero.titleMiddle} <span className="ag-shine-text">{f.hero.titleAccent2}</span></span>
          </h1>
          <p className="mt-7 max-w-2xl mx-auto text-base md:text-lg text-[#94A3B8] leading-relaxed">{f.hero.description}</p>

          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <a href="#fs-form" className="group inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-6 py-3.5 rounded-full hover:bg-white transition-colors" data-testid="fs-hero-cta-primary">
              <Sparkles className="w-4 h-4" />
              {f.hero.ctaPrimary}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a href="#fs-how" className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 rounded-full hover:border-[#00F260] hover:text-[#00F260] transition-colors" data-testid="fs-hero-cta-secondary">
              {f.hero.ctaSecondary}
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-2.5 mt-8">
            {f.hero.trust.map((label, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-xs text-[#94A3B8]">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260]" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 02 THREE ANCHORS */}
      <section className="relative pt-4 pb-14 md:pb-20 px-5 md:px-10 border-t border-white/5" data-testid="fs-anchors">
        <div className="max-w-6xl mx-auto">
          <div ref={addReveal} className="ag-reveal text-center mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.3em] text-[#00F260]">{f.anchors.tagline}</div>
          </div>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {f.anchors.items.map((a, i) => (
              <div key={i} ref={addReveal} className="ag-reveal ag-card p-7 md:p-8 relative overflow-hidden text-center">
                <div className="absolute -top-14 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />
                <div className="relative">
                  {i === 0 && f.anchors.savingsUpTo ? (
                    <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-white/90 mb-1">{f.anchors.savingsUpTo}</div>
                  ) : null}
                  <div className="font-display text-5xl md:text-7xl font-extrabold ag-shine-text leading-none">{a.v}</div>
                  <div className="mt-3 font-display text-lg md:text-xl font-bold">{a.l}</div>
                  <p className="mt-2 text-sm text-[#94A3B8]">{a.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 CALCULATOR */}
      <section className="relative pt-10 md:pt-16 pb-16 md:pb-24 px-5 md:px-10" data-testid="fs-calc">
        <div className="max-w-6xl mx-auto">
          <div ref={addReveal} className="ag-reveal text-center mb-10 md:mb-12 max-w-2xl mx-auto">
            <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.calc.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {f.calc.title}<br />
              <span className="ag-shine-text">{f.calc.titleAccent}</span>
            </h2>
            <p className="mt-4 text-[#94A3B8]">{f.calc.subtitle}</p>
          </div>

          <div ref={addReveal} className="ag-reveal relative rounded-[28px] overflow-hidden border border-[#00F260]/25" style={{ background: 'linear-gradient(180deg, #070D18 0%, #081A14 100%)' }}>
            <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#00F260]/12 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#1B93A4]/12 blur-3xl pointer-events-none" />
            <div className="absolute inset-0 ag-grid-bg opacity-[0.4] pointer-events-none" />

            <div className="relative grid lg:grid-cols-12 gap-8 md:gap-12 p-7 md:p-12">
              {/* LEFT: sliders */}
              <div className="lg:col-span-6 space-y-7">
                <SliderControl
                  icon={Truck}
                  label={f.calc.transfersLabel}
                  hint={f.calc.transfersHint}
                  value={transfers}
                  min={5}
                  max={200}
                  step={1}
                  unit={f.calc.monthlyTransfers}
                  onChange={(v) => setTransfers(v)}
                  testId="fs-calc-transfers"
                />
                <SliderControl
                  icon={Gauge}
                  label={f.calc.kmLabel}
                  hint={f.calc.kmHint}
                  value={avgKm}
                  min={50}
                  max={1500}
                  step={10}
                  unit={f.calc.kmShort}
                  onChange={(v) => setAvgKm(v)}
                  testId="fs-calc-km"
                />

                <div className="flex flex-wrap gap-3 pt-2">
                  <a href="#fs-form" className="group inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-5 py-3 rounded-full hover:bg-white transition-colors text-sm" data-testid="fs-calc-cta-primary">
                    <Sparkles className="w-4 h-4" />
                    {f.calc.ctaPrimary}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                  <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 rounded-full hover:border-[#00F260] hover:text-[#00F260] transition-colors text-sm" data-testid="fs-calc-cta-wa">
                    <MessageCircle className="w-4 h-4" />
                    {f.calc.ctaSecondary}
                  </button>
                </div>
              </div>

              {/* RIGHT: output */}
              <div className="lg:col-span-6">
                <div className="ag-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden border border-[#00F260]/30 min-h-[300px] flex flex-col justify-center">
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00F260] via-[#1B93A4] to-[#00F260]" />
                  <div className="absolute -top-20 -right-10 w-48 h-48 rounded-full bg-[#00F260]/18 blur-3xl pointer-events-none" />

                  <div className="relative z-10 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <TrendingUp className="w-4 h-4 text-[#00F260]" />
                      <span className="font-mono text-[10px] tracking-[0.25em] text-[#00F260]">{f.calc.outputLabel}</span>
                    </div>
                    <div className="font-display font-extrabold leading-none ag-shine-text text-[52px] md:text-[80px] tracking-tight" data-testid="fs-calc-output">
                      €{fmtEUR(savings.annual)}
                    </div>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.3em] text-[#475569]">{f.calc.outputUnit}</div>
                    <div className="mt-6 text-sm text-[#94A3B8]">
                      {f.calc.outputDetail} <span className="text-white font-semibold">€{fmtEUR(savings.monthly)}</span>{f.calc.outputDetailSuffix}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-[#475569] italic text-center leading-relaxed">{f.calc.note}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 HOW IT WORKS */}
      <section id="fs-how" className="relative pt-10 md:pt-16 pb-12 md:pb-20 px-5 md:px-10 border-t border-white/5" data-testid="fs-how">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.how.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {f.how.title}<br />
              <span className="ag-shine-text">{f.how.titleAccent}</span>
            </h2>
            <p className="mt-4 text-[#94A3B8]">{f.how.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {f.how.steps.map((s, i) => (
              <div key={i} ref={addReveal} className="ag-reveal ag-card ag-hover-lift p-6 md:p-8 relative overflow-hidden">
                <div className="absolute -top-6 -right-4 font-display text-7xl md:text-8xl font-extrabold text-white/5 leading-none select-none">{s.num}</div>
                <div className="relative">
                  <div className="font-mono text-[10px] tracking-[0.25em] text-[#00F260] mb-3">{s.num}</div>
                  <h3 className="font-display text-xl md:text-2xl font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-1.5 pt-4 border-t border-white/5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 05 FOR WHO */}
      <section className="relative pt-8 md:pt-12 pb-12 md:pb-20 px-5 md:px-10" data-testid="fs-forwho">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.forWho.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {f.forWho.title}<br />
              <span className="text-[#94A3B8]">{f.forWho.titleAccent}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {f.forWho.list.map((item, i) => {
              const Icon = forWhoIcons[i];
              return (
                <div key={i} ref={addReveal} className="ag-reveal ag-card ag-hover-lift p-7 md:p-8 relative overflow-hidden">
                  <div className="w-12 h-12 rounded-xl bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#00F260]" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold">{item.title}</h3>
                  <p className="mt-2.5 text-sm text-[#94A3B8] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 06 FLEET MEMBER CARD VISUAL */}
      <section className="relative pt-10 md:pt-16 pb-14 md:pb-20 px-5 md:px-10 border-t border-white/5" data-testid="fs-card-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 md:gap-14 items-center">
            <div ref={addReveal} className="ag-reveal lg:col-span-6">
              <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.cardSection.tagline}</div>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
                {f.cardSection.title}<br />
                <span className="ag-shine-text">{f.cardSection.titleAccent}</span>
              </h2>
              <p className="mt-5 text-[#94A3B8] max-w-lg">{f.cardSection.subtitle}</p>
              <ul className="mt-7 space-y-3">
                {f.cardSection.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#CBD5E1]">
                    <div className="w-6 h-6 shrink-0 rounded-md bg-[#00F260]/12 border border-[#00F260]/40 flex items-center justify-center mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260]" />
                    </div>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div ref={addReveal} className="ag-reveal lg:col-span-6">
              <FleetMemberCard language={language} t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* 07 TESTIMONIALS */}
      <section className="relative pt-10 md:pt-16 pb-12 md:pb-20 px-5 md:px-10" data-testid="fs-testimonials">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.testimonials.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {f.testimonials.title}<br />
              <span className="ag-shine-text">{f.testimonials.titleAccent}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {f.testimonials.items.map((item, i) => (
              <div key={i} ref={addReveal} className="ag-reveal ag-glass-strong rounded-2xl p-7 md:p-8 relative overflow-hidden border border-[#00F260]/15">
                <div className="absolute -top-16 -right-12 w-40 h-40 rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />
                <div className="relative">
                  <svg width="32" height="24" viewBox="0 0 32 24" className="text-[#00F260] mb-4" fill="currentColor" opacity="0.4">
                    <path d="M0 24V13C0 5.8 4 1 12 0l1.5 3C9.5 4.3 7.5 7 7.5 11H12v13H0zm20 0V13c0-7.2 4-12 12-13l1.5 3C29.5 4.3 27.5 7 27.5 11H32v13H20z" />
                  </svg>
                  <p className="font-display text-base md:text-lg text-white leading-relaxed">&ldquo;{item.quote}&rdquo;</p>
                  <div className="mt-6 pt-5 border-t border-white/5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00F260]/40 to-[#1B93A4]/40 border border-[#00F260]/40 flex items-center justify-center">
                      <span className="font-display text-base font-extrabold text-white">{item.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="font-display text-sm font-bold">{item.name}</div>
                      <div className="text-xs text-[#94A3B8]">{item.role}</div>
                      <div className="text-[11px] text-[#475569] font-mono tracking-wide mt-0.5">{item.company}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] text-[#475569] italic text-center">{f.testimonials.note}</p>
        </div>
      </section>

      {/* 08 FORM */}
      <section id="fs-form" className="relative pt-10 md:pt-16 pb-20 md:pb-28 px-5 md:px-10" data-testid="fs-form-section">
        <div className="max-w-5xl mx-auto">
          <div ref={addReveal} className="ag-reveal relative rounded-[28px] overflow-hidden border border-[#00F260]/20" style={{ background: 'linear-gradient(180deg, #060B14 0%, #081A14 100%)' }}>
            <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#00F260]/12 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#1B93A4]/12 blur-3xl pointer-events-none" />

            <div className="relative p-7 md:p-12 lg:p-16">
              <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
                <div className="lg:col-span-5">
                  <div className="font-mono text-xs tracking-[0.3em] text-[#00F260] mb-4">{f.form.tagline}</div>
                  <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-[0.95] tracking-tight">
                    {f.form.title}<br />
                    <span className="ag-shine-text">{f.form.titleAccent}</span>
                  </h2>
                  <p className="mt-4 text-[#94A3B8]">{f.form.subtitle}</p>
                  <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-[10px] tracking-[0.2em] font-mono text-[#00F260]">
                    <Lock className="w-3.5 h-3.5" />
                    {f.form.nda}
                  </div>

                  {/* Direct contact block */}
                  <div className="mt-7 pt-7 border-t border-white/5" data-testid="fs-direct-contact">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#475569] mb-4">
                      {language === 'es' ? '¿PREFIERES ESCRIBIR O LLAMAR?' : 'PREFER EMAIL OR PHONE?'}
                    </div>
                    <div className="space-y-2.5">
                      <a href="tel:+34930491501" className="group flex items-center gap-3 p-3.5 rounded-xl ag-glass border border-white/5 hover:border-[#00F260]/40 transition-colors" data-testid="fs-direct-phone">
                        <div className="w-10 h-10 rounded-lg bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-[#00F260]" />
                        </div>
                        <div className="flex-1">
                          <div className="font-mono text-[9px] tracking-[0.22em] text-[#475569]">{language === 'es' ? 'LLAMADA DIRECTA' : 'CALL DIRECT'}</div>
                          <div className="font-display text-sm md:text-base font-semibold text-white mt-0.5">+34 930 49 15 01</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#475569] group-hover:text-[#00F260] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </a>
                      <a href="mailto:hello@autogodelivery.com" className="group flex items-center gap-3 p-3.5 rounded-xl ag-glass border border-white/5 hover:border-[#00F260]/40 transition-colors" data-testid="fs-direct-email">
                        <div className="w-10 h-10 rounded-lg bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
                          <Send className="w-4 h-4 text-[#00F260]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[9px] tracking-[0.22em] text-[#475569]">{language === 'es' ? 'ESCRÍBENOS' : 'EMAIL US'}</div>
                          <div className="font-display text-sm md:text-base font-semibold text-white mt-0.5 break-all">hello@autogodelivery.com</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#475569] group-hover:text-[#00F260] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="ag-glass-strong rounded-2xl p-6 md:p-7 relative overflow-hidden border border-[#00F260]/20">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00F260] via-[#1B93A4] to-[#00F260]" />
                    {sent ? (
                      <div className="relative z-10 text-center py-10" data-testid="fs-form-success">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#00F260] flex items-center justify-center mb-5">
                          <CheckCircle2 className="w-8 h-8 text-[#060B14]" strokeWidth={3} />
                        </div>
                        <h3 className="font-display text-2xl font-bold">{f.form.successTitle}</h3>
                        <p className="mt-3 text-sm text-[#94A3B8] max-w-xs mx-auto">{f.form.successDescription}</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-3 relative z-10" data-testid="fs-form">
                        <FsField icon={Building2} label={f.form.company} name="company" value={form.company} onChange={handleChange} placeholder={f.form.companyPlaceholder} required testId="fs-company" />
                        <FsField icon={User} label={f.form.name} name="name" value={form.name} onChange={handleChange} placeholder={f.form.namePlaceholder} required testId="fs-name" />
                        <FsField icon={Send} label={f.form.email} name="email" value={form.email} onChange={handleChange} placeholder={f.form.emailPlaceholder} type="email" required testId="fs-email" />
                        <FsField icon={Phone} label={f.form.phone} name="phone" value={form.phone} onChange={handleChange} placeholder={f.form.phonePlaceholder} required testId="fs-phone" />
                        <FsField icon={Truck} label={f.form.monthlyTransfers} name="monthlyTransfers" value={form.monthlyTransfers} onChange={handleChange} placeholder={f.form.monthlyTransfersPlaceholder} required testId="fs-transfers" />

                        <button type="submit" disabled={sending} className="group w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#00F260] hover:bg-white text-[#060B14] font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60" data-testid="fs-submit">
                          {sending ? '...' : f.form.submit}
                          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                        <button type="button" onClick={handleWhatsApp} className="w-full inline-flex items-center justify-center gap-2 text-[#94A3B8] hover:text-[#00F260] py-2 text-xs font-mono uppercase tracking-widest transition-colors" data-testid="fs-wa">
                          <MessageCircle className="w-3.5 h-3.5" />
                          {f.form.whatsapp}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative pt-12 md:pt-20 pb-10 px-5 md:px-10 border-t border-white/5 overflow-hidden" data-testid="fs-footer">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl ag-glass-strong overflow-hidden flex items-center justify-center">
                  <img src={BRAND_ICON_SRC} alt="AutoGo" className="w-7 h-7" />
                </div>
                <div className="leading-none">
                  <div className="font-display text-xl font-extrabold">AUTOGO</div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-[#00F260] mt-1">{f.brandTag}</div>
                </div>
              </div>
              <p className="text-[#94A3B8] max-w-sm text-sm">{t.footer.description}</p>
              <button onClick={handleWhatsApp} className="mt-6 inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-5 py-2.5 rounded-full hover:bg-white transition-colors text-sm" data-testid="fs-footer-whatsapp">
                <MessageCircle className="w-4 h-4" />
                {t.footer.whatsapp}
              </button>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.quickLinks}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><Link to="/" className="hover:text-white transition-colors">{f.backHome}</Link></li>
                <li><a href="#fs-how" className="hover:text-white transition-colors">{f.hero.ctaSecondary}</a></li>
                <li><a href="#fs-form" className="hover:text-white transition-colors">{f.navCta}</a></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.contact}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><a href="tel:+34930491501" className="hover:text-white" data-testid="fs-footer-phone">+34 930 49 15 01</a></li>
                <li><a href="mailto:hello@autogodelivery.com" className="hover:text-white break-all" data-testid="fs-footer-email">hello@autogodelivery.com</a></li>
                <li data-testid="fs-footer-address">Carrer de Lepant 270, 08013, Barcelona, España</li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.legal}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><Link to="/politica-de-privacidad" className="hover:text-white">{t.footer.privacyPolicy}</Link></li>
                <li><Link to="/politica-de-cookies" className="hover:text-white">{t.footer.cookiesPolicy}</Link></li>
              </ul>
            </div>
          </div>

          <div className="relative pt-8 border-t border-white/5">
            <div aria-hidden className="font-display font-extrabold tracking-tight text-[18vw] leading-none text-white/[0.03] select-none pointer-events-none whitespace-nowrap text-center -mb-12 md:-mb-20">
              AUTOGO
            </div>
            <div className="relative flex flex-col items-center justify-center text-xs text-[#475569] z-10 text-center">
              <div>{t.footer.rights}</div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <button
        onClick={handleWhatsApp}
        className="fixed z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_8px_30px_rgba(37,211,102,0.4)] transition-transform hover:scale-105 max-sm:right-4 max-sm:bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] sm:bottom-6 sm:right-6"
        aria-label="WhatsApp"
        data-testid="fs-whatsapp-float"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    </div>
  );
};

/* -------- Subcomponents -------- */

const FsField = ({ icon: Icon, label, name, value, onChange, placeholder, type = 'text', required = false, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />}
      <input
        className="ag-input"
        style={{ paddingLeft: '2.5rem' }}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
      />
    </div>
  </div>
);

const SliderControl = ({ icon: Icon, label, hint, value, min, max, step, unit, onChange, testId }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#00F260]" />
          </div>
          <div>
            <div className="font-display text-base font-bold">{label}</div>
            <div className="text-[11px] text-[#475569]">{hint}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-extrabold text-white leading-none">{value.toLocaleString()}</div>
          <div className="font-mono text-[9px] tracking-[0.22em] text-[#00F260] mt-1">{unit}</div>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="fs-slider w-full"
        style={{
          background: `linear-gradient(90deg, #00F260 0%, #00F260 ${pct}%, rgba(255,255,255,0.08) ${pct}%, rgba(255,255,255,0.08) 100%)`
        }}
        data-testid={testId}
      />
    </div>
  );
};

const FleetMemberCard = ({ language, t }) => {
  const m = t.operatingMargin;
  return (
    <div className="ag-animate-float">
      <div className="relative aspect-[16/10] max-w-[480px] mx-auto rounded-2xl overflow-hidden border border-[#00F260]/30" style={{ background: 'linear-gradient(135deg, #000000 0%, #0A1623 55%, #003820 100%)' }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(0,242,96,0.22),transparent_55%)]" />
        <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-[#00F260]/15 blur-3xl" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.08]" viewBox="0 0 400 250">
          <defs>
            <pattern id="fs-card-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#00F260" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="400" height="250" fill="url(#fs-card-grid)" />
        </svg>
        <div className="absolute -top-10 -right-10 w-32 h-[200%] rotate-[18deg] bg-gradient-to-b from-transparent via-[#00F260]/8 to-transparent" />
        <div className="relative h-full p-5 md:p-6 flex flex-col justify-between text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-[#00F260]">{m.cardLabel}</div>
              <div className="font-display text-lg md:text-xl font-extrabold mt-1 tracking-tight">AUTOGO</div>
              <div className="font-mono text-[8px] tracking-[0.25em] text-[#94A3B8] mt-1">CORPORATE · TIER PLATINUM</div>
            </div>
            <div className="relative w-11 h-8 rounded-md bg-gradient-to-br from-[#FFD27A] via-[#A88842] to-[#5A4220] border border-[#FFD27A]/50 overflow-hidden">
              <div className="absolute inset-1 grid grid-cols-3 grid-rows-3 gap-[1px]">
                {Array.from({ length: 9 }).map((_, k) => (
                  <div key={k} className="bg-[#5A4220]/60 rounded-[1px]" />
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 my-2">
            <div>
              <div className="font-mono text-[8px] tracking-[0.2em] text-[#475569]">{language === 'es' ? 'AHORRO YTD' : 'YTD SAVINGS'}</div>
              <div className="font-display text-sm font-bold text-[#00F260] mt-0.5">€4.280</div>
            </div>
            <div>
              <div className="font-mono text-[8px] tracking-[0.2em] text-[#475569]">{language === 'es' ? 'TRASLADOS' : 'TRANSFERS'}</div>
              <div className="font-display text-sm font-bold mt-0.5">42 / 80</div>
            </div>
            <div>
              <div className="font-mono text-[8px] tracking-[0.2em] text-[#475569]">{language === 'es' ? 'TARIFA' : 'RATE'}</div>
              <div className="font-display text-sm font-bold mt-0.5 inline-flex items-center gap-1">
                <Lock className="w-2.5 h-2.5 text-[#00F260]" />
                {language === 'es' ? 'Fija' : 'Locked'}
              </div>
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] text-[#94A3B8] tracking-[0.3em]">{m.cardId}</div>
            <div className="flex items-end justify-between mt-1">
              <div className="font-display text-lg md:text-xl font-bold tracking-tight">{m.name}</div>
              <div className="font-mono text-[10px] text-[#00F260]">·····12</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetSaveLanding;
